import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './pharmacy-refund-bill.reducer';

export const PharmacyRefundBill = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const pharmacyRefundBillList = useAppSelector(state => state.pharmacyRefundBill.entities);
  const loading = useAppSelector(state => state.pharmacyRefundBill.loading);
  const totalItems = useAppSelector(state => state.pharmacyRefundBill.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="pharmacy-refund-bill-heading" data-cy="PharmacyRefundBillHeading">
        <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.home.title">Pharmacy Refund Bills</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/pharmacy-refund-bill/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.home.createLabel">Create new Pharmacy Refund Bill</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.pharmacyRefundBill.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {pharmacyRefundBillList && pharmacyRefundBillList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('billNumber')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.billNumber">Bill Number</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('billNumber')} />
                </th>
                <th className="hand" onClick={sort('billDate')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.billDate">Bill Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('billDate')} />
                </th>
                <th className="hand" onClick={sort('refundAmount')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.refundAmount">Refund Amount</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('refundAmount')} />
                </th>
                <th className="hand" onClick={sort('totalCost')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.totalCost">Total Cost</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalCost')} />
                </th>
                <th className="hand" onClick={sort('discount')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.discount">Discount</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('discount')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('cgst')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.cgst">Cgst</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cgst')} />
                </th>
                <th className="hand" onClick={sort('sgst')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.sgst">Sgst</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sgst')} />
                </th>
                <th className="hand" onClick={sort('patientType')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.patientType">Patient Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('patientType')} />
                </th>
                <th className="hand" onClick={sort('printed')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.printed">Printed</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('printed')} />
                </th>
                <th className="hand" onClick={sort('printCount')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.printCount">Print Count</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('printCount')} />
                </th>
                <th className="hand" onClick={sort('voided')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.voided">Voided</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('voided')} />
                </th>
                <th className="hand" onClick={sort('voidedReason')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.voidedReason">Voided Reason</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('voidedReason')} />
                </th>
                <th className="hand" onClick={sort('voidedDate')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.voidedDate">Voided Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('voidedDate')} />
                </th>
                <th className="hand" onClick={sort('roundOffVal')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.roundOffVal">Round Off Val</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('roundOffVal')} />
                </th>
                <th className="hand" onClick={sort('billFor')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.billFor">Bill For</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('billFor')} />
                </th>
                <th className="hand" onClick={sort('billForId')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.billForId">Bill For Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('billForId')} />
                </th>
                <th className="hand" onClick={sort('status')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.status">Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('status')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.pharmacy">Pharmacy</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.glucoUser">Gluco User</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.pharmacist">Pharmacist</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.saleBill">Sale Bill</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.returnBill">Return Bill</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.admission">Admission</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.consumer">Consumer</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pharmacyRefundBillList.map((pharmacyRefundBill, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/pharmacy-refund-bill/${pharmacyRefundBill.id}`} color="link" size="sm">
                      {pharmacyRefundBill.id}
                    </Button>
                  </td>
                  <td>{pharmacyRefundBill.billNumber}</td>
                  <td>{pharmacyRefundBill.billDate}</td>
                  <td>{pharmacyRefundBill.refundAmount}</td>
                  <td>{pharmacyRefundBill.totalCost}</td>
                  <td>{pharmacyRefundBill.discount}</td>
                  <td>{pharmacyRefundBill.active ? 'true' : 'false'}</td>
                  <td>{pharmacyRefundBill.cursor}</td>
                  <td>{pharmacyRefundBill.timestampentry}</td>
                  <td>{pharmacyRefundBill.microSec}</td>
                  <td>{pharmacyRefundBill.lastupdated}</td>
                  <td>{pharmacyRefundBill.cgst}</td>
                  <td>{pharmacyRefundBill.sgst}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.PatientType.${pharmacyRefundBill.patientType}`} />
                  </td>
                  <td>{pharmacyRefundBill.printed ? 'true' : 'false'}</td>
                  <td>{pharmacyRefundBill.printCount}</td>
                  <td>{pharmacyRefundBill.voided ? 'true' : 'false'}</td>
                  <td>{pharmacyRefundBill.voidedReason}</td>
                  <td>{pharmacyRefundBill.voidedDate}</td>
                  <td>{pharmacyRefundBill.roundOffVal}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.BillFor.${pharmacyRefundBill.billFor}`} />
                  </td>
                  <td>{pharmacyRefundBill.billForId}</td>
                  <td>{pharmacyRefundBill.status}</td>
                  <td>
                    {pharmacyRefundBill.pharmacy ? (
                      <Link to={`/pharmacy/${pharmacyRefundBill.pharmacy.id}`}>{pharmacyRefundBill.pharmacy.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyRefundBill.glucoUser ? (
                      <Link to={`/gluco-user/${pharmacyRefundBill.glucoUser.id}`}>{pharmacyRefundBill.glucoUser.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyRefundBill.pharmacist ? (
                      <Link to={`/tenant-employee/${pharmacyRefundBill.pharmacist.id}`}>{pharmacyRefundBill.pharmacist.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyRefundBill.saleBill ? (
                      <Link to={`/pharmacy-bill/${pharmacyRefundBill.saleBill.id}`}>{pharmacyRefundBill.saleBill.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyRefundBill.returnBill ? (
                      <Link to={`/pharmacy-return-bill/${pharmacyRefundBill.returnBill.id}`}>{pharmacyRefundBill.returnBill.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyRefundBill.admission ? (
                      <Link to={`/admission/${pharmacyRefundBill.admission.id}`}>{pharmacyRefundBill.admission.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyRefundBill.consumer ? (
                      <Link to={`/pharmacy/${pharmacyRefundBill.consumer.id}`}>{pharmacyRefundBill.consumer.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/pharmacy-refund-bill/${pharmacyRefundBill.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/pharmacy-refund-bill/${pharmacyRefundBill.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/pharmacy-refund-bill/${pharmacyRefundBill.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.pharmacyRefundBill.home.notFound">No Pharmacy Refund Bills found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={pharmacyRefundBillList && pharmacyRefundBillList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default PharmacyRefundBill;
